import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, Copy, Radio, MonitorPlay, PanelRight, MessageSquare, BellRing, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const Light = ({ ok, id }) => (
  ok === null ? <span className="text-[10px] font-mono text-slate-500" data-testid={`setup-light-${id}`} data-state="na">—</span>
    : ok ? <CheckCircle2 className="w-5 h-5 text-[#22e3a1] drop-shadow-[0_0_6px_rgba(34,227,161,0.7)]" data-testid={`setup-light-${id}`} data-state="ok" />
      : <CircleDashed className="w-5 h-5 text-rose-400 animate-pulse" data-testid={`setup-light-${id}`} data-state="wait" />
);

const Step = ({ n, icon: Icon, id, title, how, link, ok, t }) => {
  const copy = () => { navigator.clipboard?.writeText(link); toast.success(t("setup.copied")); };
  return (
    <div className={`rounded-xl border p-3 flex gap-3 ${ok ? "border-[#22e3a1]/35 bg-[#22e3a1]/[0.04]" : "border-white/8 bg-black/25"}`} data-testid={`setup-step-${id}`}>
      <div className="w-8 h-8 shrink-0 rounded-lg grid place-items-center bg-[#a746ff]/15 border border-[#a746ff]/40 text-[#d9b8ff] font-mono text-sm">{n}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold text-white"><Icon className="w-4 h-4 text-[#c79bff]" />{title}</div>
        <p className="text-[11px] text-slate-400 mt-0.5">{how}</p>
        {link && (
          <button onClick={copy} data-testid={`setup-copy-${id}`} className="mt-2 cx-metal rounded-lg px-3 py-1.5 text-[11px] flex items-center gap-1.5 max-w-full">
            <Copy className="w-3.5 h-3.5 shrink-0" /><span className="truncate font-mono">{link}</span>
          </button>
        )}
      </div>
      <Light ok={ok} id={id} />
    </div>
  );
};

export const LiveSetupWizard = ({ overlay, panel, refreshKey }) => {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  useEffect(() => {
    const load = () => api.get("/live/setup-status").then((r) => setS(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [refreshKey]);
  const steps = [
    { id: "live", icon: Radio, ok: s ? s.live : false, title: t("setup.live_t"), how: t("setup.live_h") },
    { id: "overlay", icon: MonitorPlay, ok: s ? s.overlay : false, title: t("setup.ov_t"), how: t("setup.ov_h"), link: overlay && `${overlay}?mod=1` },
    { id: "dock", icon: PanelRight, ok: s ? s.dock : false, title: t("setup.dock_t"), how: t("setup.dock_h"), link: panel && `${panel}?dock=1` },
    { id: "chat", icon: MessageSquare, ok: s ? s.chat : null, title: t("setup.chat_t"), how: s?.chat === null ? t("setup.chat_na") : t("setup.chat_h") },
    { id: "push", icon: BellRing, ok: s ? s.push : false, title: t("setup.push_t"), how: t("setup.push_h") },
  ];
  const done = steps.filter((x) => x.ok).length;
  const total = steps.filter((x) => x.ok !== null).length;
  return (
    <section className="cx-card rounded-2xl p-5 mt-6 relative overflow-hidden" data-testid="live-setup-wizard">
      <div className="flex items-center gap-3 mb-1">
        <Wand2 className="w-5 h-5 text-[#c79bff]" />
        <h2 className="font-display font-bold text-white text-base md:text-lg">{t("setup.title")}</h2>
        <span className={`ml-auto text-xs font-mono px-2 py-0.5 rounded-full border ${done === total ? "text-[#22e3a1] border-[#22e3a1]/40" : "text-[#d9b8ff] border-[#a746ff]/40"}`} data-testid="setup-progress">{done}/{total}</span>
      </div>
      <p className="text-xs text-slate-400 mb-4">{done === total ? t("setup.all_ok") : t("setup.desc")}</p>
      <div className="grid md:grid-cols-2 gap-3">{steps.map((x, i) => <Step key={x.id} n={i + 1} {...x} t={t} />)}</div>
    </section>
  );
};
